import * as THREE from 'three';
import { skyColourAlong } from '@/render/sky';

/**
 * The open sea: one huge plane with a shader shimmer. Render-only — like the
 * clouds and birds, the water's motion is pure theatre and nothing in the
 * world may read it. The caller recentres the mesh on the focus each frame;
 * the shimmer is anchored in world XZ, so the pattern never slides with it.
 *
 * The surface knows how deep the seabed is beneath it: a coarse depth texture
 * baked once from the region heightfield grades colour and opacity from
 * translucent shallows to opaque deep water, and a soft animated foam band
 * hugs the shoreline where the depth runs out.
 *
 * The geometry stays a dead-flat plane: this is shimmer, not waves.
 *
 * ---------------------------------------------------------------------------
 * WATER IS A MIRROR, AND THIS SURFACE HAD NEVER BEEN TOLD SO
 * ---------------------------------------------------------------------------
 *
 * Everything above describes a shader that decides the sea's colour from ONE
 * input: how deep the seabed is. Nothing in it read the view direction. That
 * is the wrong question to ask water, because water is a Fresnel surface — at
 * normal incidence you see through it to the column below, and at grazing
 * incidence it is a mirror of the sky, and the crossover is violent.
 *
 * `tools/sea.ts` measured what that costs, on the real island, at the altitude
 * the screenshot gate uses. The visible ocean from 300 m sits between 75° and
 * 88° off the normal, so **23% of the average ocean pixel should be sky** and
 * all of it was seabed. Worse, the error is not a constant offset: depth grows
 * with distance from shore and so does the reflected share, so the two effects
 * point opposite ways. Measured over a real west-facing frame:
 *
 *     correlation( Fresnel , shipped luminance ) = -0.940
 *
 * Water demands +1. The sea was doing almost exactly the opposite of what a sea
 * does — brightest in the near shallows where it should be darkest and most
 * transparent, darkest in the mid-distance where it should be turning to sky.
 * No amount of retuning the turquoise could reach that; it is not a palette
 * fault, it is a missing term.
 *
 * Three things follow, and they are the whole change:
 *
 *  1. **`metalness` was 0.28 and there is no environment map in this scene.**
 *     Metalness tints the specular by the base colour and removes the diffuse
 *     in proportion — so on a scene with no envMap it was deleting 28% of the
 *     water's own colour and spending it on a reflection that had nothing to
 *     sample. Water is a dielectric. It is 0 now, which is both correct and
 *     what makes room for a real reflection.
 *
 *  2. **The reflection is added explicitly**, as an envMap would have, using
 *     exact Fresnel against the *perturbed* normal — so each wave face carries
 *     its own reflectance and the surface reads as water rather than as a
 *     tinted sheet of glass. Exact rather than Schlick because Schlick is
 *     calibrated at normal incidence and this problem lives at 75–88°, where
 *     `tools/sea.ts` measures it 5.7 percentage points too bright.
 *
 *  3. **The wave bands are a LOD now, and one of them is new.** Measured, with
 *     the foreshortening the depression angle imposes: the 40 m swell is 43.6
 *     px at 500 m, 12.2 px at 1 km and 3.15 px at 2 km; the 9 m chop is 2.74 px
 *     at 1 km; the 3 m ripple is 0.91 px at *one kilometre*. Past those ranges
 *     they are not detail, they are sub-pixel flicker, so each band now fades
 *     at its own measured range. That alone would leave the far sea dead flat —
 *     which is exactly what the 3 km frame showed — so there is a fourth,
 *     longer band at ~300 m, chosen because it is 23.7 px at 2 km from 300 m
 *     altitude and still 6.1 px at 10 km from 3 km altitude. It is the only
 *     structure the far ocean has.
 *
 * WHERE THE SKY COLOUR COMES FROM, and why it needs nothing from anyone.
 * A flat sea's normal is world-up, so a view ray depressed by β reflects to a
 * ray *elevated* by β — and `tools/sea.ts` §6 measures the whole visible ocean
 * as mirroring the sky between **6.9° and 28.6°** above the horizon. That is
 * the haze band: the same low sky `paintSky` tunes `fog.color` to, and the same
 * band `render/aerial.ts` lifts every distant surface toward. So the reflection
 * samples `fogColor`, which every fogged material already carries as a uniform.
 * Measured, not assumed: the sRGB form of `fog.color` is `#9fb0bd` against the
 * sky's own rendered horizon pixel of `rgb(160,180,197)` — within 3%. The sea
 * and the haze therefore cannot drift apart at the horizon, by construction,
 * and the day the sky's colour changes the ocean follows it for free.
 */

/** Depth (metres below sea level) beyond which the texture saturates. */
const DEPTH_RANGE = 64;

export function createSea(
  extent: number,
  depth: { at(wx: number, wz: number): number; worldExtent: number },
): { mesh: THREE.Mesh; update(tSec: number, camera?: THREE.Camera): void } {
  const geo = new THREE.PlaneGeometry(extent, extent);
  geo.rotateX(-Math.PI / 2);

  // bake the seabed once: one byte per ~50 m is plenty for colour grading,
  // and clamp-to-edge extends the rim's deep water out to the sea's far edge
  const RES = 1024;
  const data = new Uint8Array(RES * RES);
  const half = depth.worldExtent / 2;
  const step = depth.worldExtent / RES;
  for (let j = 0; j < RES; j++) for (let i = 0; i < RES; i++) {
    const d = -depth.at(-half + (i + 0.5) * step, -half + (j + 0.5) * step);
    data[j * RES + i] = Math.round(THREE.MathUtils.clamp(d / DEPTH_RANGE, 0, 1) * 255);
  }
  const depthTex = new THREE.DataTexture(data, RES, RES, THREE.RedFormat);
  depthTex.magFilter = depthTex.minFilter = THREE.LinearFilter;
  depthTex.wrapS = depthTex.wrapT = THREE.ClampToEdgeWrapping;
  depthTex.needsUpdate = true;

  // colour/alpha come from the depth grade in the shader; the material only
  // supplies the light response.
  //
  // `metalness` is 0 and must stay 0: water is a dielectric, and this scene
  // has no environment map, so any metalness at all trades the water's own
  // colour for a reflection with nothing to reflect. The sky reflection this
  // surface *does* want is added explicitly below, where its strength can be
  // the real Fresnel term rather than a constant.
  //
  // `roughness` stays low so the sun keeps a tight glint on the near water —
  // the wave-slope variance measured across all four bands is only ~0.03 rms,
  // which is a GGX roughness near 0.2, so the shipped 0.16 is already about
  // right and the far-field fade needs no roughness LOD to go with it.
  const mat = new THREE.MeshStandardMaterial({
    color: 0xffffff, roughness: 0.16, metalness: 0.0,
    transparent: true, opacity: 1,
  });

  const uT = { value: 0 };
  /*
   * The two ends of the strip of sky this ocean can see.
   *
   * A flat sea reflects a view ray depressed by β into the sky at elevation β,
   * and tools/sea.ts §6 measures the whole visible ocean as spanning 6.9° to
   * 28.6°. So two samples bracket it, and the shader lerps between them by the
   * fragment's own angle. They are seeded pale so the first frame — which runs
   * before `paintSky` has ever been called — is a hazy sea rather than a black
   * one.
   */
  const uSkyLo = { value: new THREE.Color(0.62, 0.69, 0.74) };
  const uSkyHi = { value: new THREE.Color(0.52, 0.62, 0.72) };
  const SKY_LO = 3 * Math.PI / 180, SKY_HI = 26 * Math.PI / 180;
  const sampleDir = new THREE.Vector3();

  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uT = uT;
    sh.uniforms.uSkyLo = uSkyLo;
    sh.uniforms.uSkyHi = uSkyHi;
    sh.uniforms.uDepth = { value: depthTex };
    sh.uniforms.uInvExtent = { value: 1 / depth.worldExtent };
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vSeaW;')
      .replace('#include <begin_vertex>',
        '#include <begin_vertex>\nvSeaW = (modelMatrix * vec4(position, 1.0)).xyz;');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>
      varying vec3 vSeaW;
      uniform float uT;
      uniform sampler2D uDepth;
      uniform float uInvExtent;
      uniform vec3 uSkyLo;
      uniform vec3 uSkyHi;
      // carried between the injected blocks below, which the chunk order puts
      // in three different places inside main(). Initialised at declaration:
      // GLSL ES leaves an uninitialised global undefined, and these are read
      // in a block that a driver could reach before the block that writes them.
      float gSeaFresnel = 0.0;
      vec3 gSeaSky = vec3(0.62, 0.69, 0.74);

      /**
       * Exact unpolarised Fresnel reflectance, air -> water (n = 1.333).
       *
       * Schlick is the habit here and it is wrong for this surface: it is
       * calibrated at normal incidence, and \`tools/sea.ts\` measures it 5.7
       * percentage points too reflective at 82° off the normal — which is
       * where most of the ocean in any played frame actually sits. Two sqrts
       * and two divides buy the horizon back.
       */
      float seaFresnel(float cosI) {
        float sinT = sqrt(max(0.0, 1.0 - cosI * cosI)) / 1.333;
        float cosT = sqrt(max(0.0, 1.0 - sinT * sinT));
        float rs = (cosI - 1.333 * cosT) / (cosI + 1.333 * cosT);
        float rp = (cosT - 1.333 * cosI) / (cosT + 1.333 * cosI);
        return clamp(0.5 * (rs * rs + rp * rp), 0.0, 1.0);
      }`)
      // the water column: translucent turquoise over the shelf, saturating
      // navy past it, with a breaking line where the seabed meets the light
      .replace('#include <color_fragment>', `#include <color_fragment>
      {
        float d = texture2D(uDepth, vSeaW.xz * uInvExtent + 0.5).r * ${DEPTH_RANGE.toFixed(1)};
        /*
         * The three water-column colours are the shipped ones times 0.72, and
         * that number is not taste — it is the exact factor dropping metalness
         * gave back. Three's PBR computes the diffuse albedo as
         * \`diffuseColor.rgb * (1.0 - metalness)\`, so every one of these tones
         * had been multiplied by (1 - 0.28) = 0.72 for its whole life, and the
         * eye that chose them was choosing what it saw AFTER that. Removing
         * metalness without this would have silently brightened the sea 39%
         * and called it a reflection — and it did: measured with the haze
         * pushed out of frame, the mid-water came back 58 luminance up when
         * Fresnel alone accounts for about 42 of it.
         *
         * So the column keeps exactly the tone it always had, and the sky
         * reflection is added on top of it rather than mixed into a brightened
         * version of it. Deep water stays deep, which is what makes the far
         * sea's silver read as a surface rather than as washed-out paint.
         */
        vec3 wc = mix(vec3(0.216, 0.418, 0.389), vec3(0.072, 0.245, 0.310), smoothstep(1.0, 14.0, d));
        wc = mix(wc, vec3(0.040, 0.122, 0.209), smoothstep(14.0, 52.0, d));
        diffuseColor.rgb = wc;
        diffuseColor.a = mix(0.34, 0.90, smoothstep(0.0, 16.0, d));
        /*
         * Foam: a soft band hugging the shore, alive but never a hard ring.
         *
         * It used to be sin(x * 0.21) * sin(z * 0.26) — a SEPARABLE product
         * of two axis-aligned sines, which is not a wave, it is a 30 x 24 m
         * lattice. On a flat sea nobody could see it; over the Fresnel surface
         * it photographed as a checkerboard lying on the beach, and it was the
         * loudest artifact left in the frame.
         *
         * A breaking wave is a line of constant depth. So the phase runs off
         * d — the depth the shader already has — and every band becomes an
         * iso-depth contour, which means the surf follows the coastline's own
         * shape for nothing, bends into bays and wraps headlands, and cannot
         * form a lattice because depth has no axes. The one extra sine is a
         * ~256 m along-coast modulation so some stretches break harder than
         * others; its wavevector is deliberately off both axes.
         */
        float shore = 1.0 - smoothstep(0.1, 2.8, d);
        float swash = 0.5 + 0.5 * sin(d * 3.4 - uT * 1.6
                    + 1.1 * sin(dot(vSeaW.xz, vec2(0.0131, -0.0207))));
        float foam = shore * swash;
        diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.88, 0.93, 0.93), foam * 0.8);
        diffuseColor.a = max(diffuseColor.a, foam * 0.85);
      }`)
      // after the stock normal is settled, retilt it from the wave fields —
      // the plane's true normal is world-up, so we can build the perturbed
      // normal in world space and carry it into view space whole
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
      {
        vec3 toEyeW = cameraPosition - vSeaW;
        float dist = length(toEyeW);
        toEyeW /= max(dist, 1e-4);

        /*
         * FOUR wave bands, each fading at the range its own wavelength stops
         * being resolvable. Measured in tools/sea.ts §3, foreshortened by
         * the depression angle — a swell seen 8° above edge-on presents only
         * sin(8°) of its length to the eye:
         *
         *     wavelength     0.5 km    1 km     2 km      5 km    10 km
         *     swell   300 m    —      —       23.7 px    —      6.07 px
         *     swell    40 m  43.6 px  12.2 px  3.15 px  0.51 px  0.13 px
         *     chop      9 m   9.82 px  2.74 px 0.71 px  0.11 px  0.03 px
         *     ripple    3 m   3.27 px  0.91 px 0.24 px  0.04 px  0.01 px
         *
         * A mark under ~3 px is not a signal and under 1 px does not exist —
         * it is aliasing, which on a normal field reads as crawling glitter.
         * So each band is cut just past where it stops paying, and the 300 m
         * swell exists at all because without it every band is gone by 2 km
         * and the far ocean is a dead gradient. It was: the 3 km frame showed
         * nothing on the water but cloud shadows.
         *
         * Speeds follow the deep-water dispersion relation, w = sqrt(g|k|), so
         * long waves outrun short ones the way real swell does. The three
         * original bands keep their hand-tuned speeds — they were chosen by
         * eye against this same surface and are close enough to the relation
         * to be worth leaving alone.
         */
        /*
         * The four wavevectors keep their magnitudes and are turned apart.
         *
         * As shipped they pointed at 30.0°, -31.9° and 32.5° — three bands on
         * one axis and one on its mirror, which is a diamond lattice, which is
         * a plaid. It was invisible while the surface had no view-dependent
         * response and became obvious the moment Fresnel started amplifying
         * slope near the horizon: the first frame after the reflection landed
         * was corrugated. Same magnitudes, so every pixel figure measured in
         * tools/sea.ts still holds; only the headings move.
         */
        vec2 k0 = vec2( 0.01660,  0.01280);    // |k0| ~ 2pi/300     37.6°
        vec2 k1 = vec2( 0.15278, -0.04093);    // |k1| ~ 2pi/40     -15.0°
        vec2 k2 = vec2( 0.06092,  0.69630);    // |k2| ~ 2pi/9       85.0°
        vec2 k3 = vec2(-1.56740,  1.56740);    // |k3| ~ 2pi/3      135.0°

        /*
         * …and the long swell's crests are bent, because a pure sine has
         * infinitely long straight ones and the ocean does not. The phase is
         * warped along the crest direction on a ~1.4 km period, which walks
         * each crest about 40 m sideways over its length — the cheapest thing
         * that turns a corrugated sheet back into a sea, at one extra sine.
         */
        vec2 k0perp = vec2(-k0.y, k0.x);
        float crest = 0.85 * sin(dot(vSeaW.xz, k0perp * 0.22) + uT * 0.11);

        float fSwellL = 1.0 - smoothstep(14000.0, 30000.0, dist);
        float fSwell  = 1.0 - smoothstep( 1800.0,  4500.0, dist);
        float fChop   = 1.0 - smoothstep(  420.0,  1400.0, dist);
        float fRipple = 1.0 - smoothstep(  140.0,   560.0, dist);

        // magnitudes are SLOPES, kept gentle. 0.021 on the long swell is a
        // real 2 m sea over a 300 m wavelength, not a number picked to look
        // busy — at grazing incidence a slope that small still moves the
        // reflected ray by more than a degree, and near the horizon that is
        // several percent of Fresnel, which is what makes it visible at all.
        vec2 g = normalize(k0) * 0.021 * cos(dot(vSeaW.xz, k0) + crest + uT * 0.452) * fSwellL
               + normalize(k1) * 0.035 * cos(dot(vSeaW.xz, k1) + uT * 0.50)  * fSwell
               + normalize(k2) * 0.020 * cos(dot(vSeaW.xz, k2) + uT * 1.30)  * fChop
               + normalize(k3) * 0.011 * cos(dot(vSeaW.xz, k3) + uT * 2.60)  * fRipple;
        vec3 nw = normalize(vec3(-g.x, 1.0, -g.y));
        normal = normalize((viewMatrix * vec4(nw, 0.0)).xyz);

        // the reflected share of THIS fragment, off the wave face rather than
        // off the flat plane — which is the whole reason a sea has texture at
        // a distance where its geometry is long gone
        float cosI = clamp(dot(nw, toEyeW), 0.0, 1.0);
        gSeaFresnel = seaFresnel(cosI);

        // …and WHICH sky it reflects. cosI is the sine of the reflected ray's
        // elevation, so it indexes the strip directly: sin(3°) = 0.0523 at the
        // horizon end, sin(26°) = 0.4384 at the steep end.
        gSeaSky = mix(uSkyLo, uSkyHi, smoothstep(0.0523, 0.4384, cosI));

        /*
         * A reflecting surface is an opaque one. The water column's alpha was
         * the only thing deciding how much of the seabed showed through, and
         * over far shallows that is 0.34 — so without this the mirror would be
         * painted onto a fragment two thirds of which is whatever lies behind.
         * Reflectance is a floor on opacity: you cannot see through the part
         * of the surface that is busy being a mirror.
         */
        diffuseColor.a = max(diffuseColor.a, gSeaFresnel);
      }`)
      /*
       * …and the reflection itself, after the frame has been tone-mapped and
       * encoded, which is a deliberate choice rather than a convenience.
       *
       * `skyColourAlong` returns the dome's colour in DISPLAY space — already
       * through the sky's own ACES curve and gamma — so mixing here, after
       * `colorspace_fragment`, is the one place the two are directly
       * comparable. It buys a guarantee no pre-tonemap mix could: when Fresnel
       * reaches 1 the sea displays *exactly* the colour the sky is, so the
       * ocean, the horizon and the dome meet with no seam and no second set of
       * constants to keep in step. `render/aerial.ts` then eats the result on
       * the same fogNear/fogFar as every other surface.
       *
       * What it costs: the sun's own specular has already been tone-mapped by
       * this point, so mixing over it attenuates the glint by (1 - F). That
       * double-counts Fresnel on the sun alone. It is small where it matters —
       * F is 7–13% across the near water where the glitter track actually
       * lives, and where F is large (the far horizon) the sky reflection IS
       * what the glitter would have been.
       */
      .replace('#include <colorspace_fragment>', `#include <colorspace_fragment>
      {
        gl_FragColor.rgb = mix(gl_FragColor.rgb, gSeaSky, gSeaFresnel);
      }`);
  };

  const mesh = new THREE.Mesh(geo, mat);
  mesh.receiveShadow = true;
  return {
    mesh,
    /**
     * `camera`, when the caller has one to give, aims the two sky samples down
     * the eye's own bearing. Without it they are taken along -Z.
     *
     * What that costs, measured off the live sky uniforms — the spread in sky
     * luminance across eight azimuths, at the two elevations sampled:
     *
     *     midday,  sun 59.7° up     el  3°: 0.5      el 26°: 15.6
     *     low sun, sun -11.4°       el  3°: 26.8     el 26°:  7.6
     *
     * So a fixed bearing is worth half a luminance level at the midday horizon
     * — the screenshot gate cannot see it — because the sun's wide glow rides
     * `pow(dot(v, sun), 7)` and a sun 60° up contributes almost nothing to a
     * ray 3–26° above the horizon whatever its bearing. It earns its keep at a
     * LOW sun, where 26.8 lands at exactly the elevation carrying 44–74%
     * Fresnel: about 12–20 luminance on screen, and the difference between a
     * silver path running toward the sun and a sea that glows evenly all over.
     *
     * Note the ordering: main.ts calls this at :1733 and `paintSky` at :1737,
     * so the samples are one frame stale. At the shipped clock a frame is
     * ~2.9 world-minutes of sun travel, which is below the quantisation of the
     * colour itself; it is recorded here so nobody mistakes it for a bug.
     */
    update(tSec: number, camera?: THREE.Camera) {
      uT.value = tSec;
      let ax = 0, az = -1;
      if (camera) {
        const e = camera.matrixWorld.elements;
        ax = -e[8]; az = -e[10];
        const l = Math.hypot(ax, az);
        if (l > 1e-6) { ax /= l; az /= l; } else { ax = 0; az = -1; }
      }
      sampleDir.set(ax * Math.cos(SKY_LO), Math.sin(SKY_LO), az * Math.cos(SKY_LO));
      skyColourAlong(sampleDir, uSkyLo.value);
      sampleDir.set(ax * Math.cos(SKY_HI), Math.sin(SKY_HI), az * Math.cos(SKY_HI));
      skyColourAlong(sampleDir, uSkyHi.value);
    },
  };
}
